<template>
  <div :class="classNameToUse">
    <div v-if="isCameraViewer" class="group_item_camera_viewer">
      <app-camera-viewer :camera="camera"></app-camera-viewer>
    </div>
    <div v-else>
      <div v-if="isArticleList">
        <app-article-list :content="stateValue"></app-article-list>
      </div>
      <div v-else>
        <div v-if="hasControlSlider">
          <app-control-slider
              :propValue="sliderValue"
              :disabled="!mqttConnected || valueForDisabledStateTopic"
              :presets="parsedExtraToPresets"
              :onChange="handleSliderChange"
          ></app-control-slider>
        </div>
        <div class="group_item_simple" v-else>
          <div class="left">
            <router-link :to="{path: urlForLink}">{{name}}</router-link>
          </div>
          <div class="right">
            <div @click.prevent="handleClick" v-if="hasSwitch">
              <b-switch
                  :disabled="!mqttConnected || valueForDisabledStateTopic"
                  :type="switchType"
                  v-model="switchValue"
                  :true-value="1"
                  :false-value="0"
              ></b-switch>
            </div>
            <div v-else>
              <div v-if="hasDropdown">
                <b-select v-model="dropdownValue" @change.native="handleChange($event)">
                  <option
                      v-for="value in dropdownSourceValues"
                      :key="value"
                      :value="value"
                  >
                    {{value}}{{dropdownOptionSufix}}
                  </option>
                </b-select>
              </div>
              <div v-else>
                <div v-if="hasActionButton">
                  <app-action-button
                      :disabled="!mqttConnected"
                      :label="data.object_suffix"
                      :onClick="handleActionButtonClick"
                  ></app-action-button>
                </div>
                <div v-else>
                  <div v-if="hasControlDropdown">
                    <app-control-dropdown
                        :textMappings="valueForDropdownMapping"
                        :propValue="dropdownValue"
                        :suffix="data.object_suffix"
                        :onChange="handleDropdownValueChange"
                    ></app-control-dropdown>
                  </div>
                  <div v-else class="stateValue">
                    <app-long-text :value="translateStateValue(stateValue)" :length="16"></app-long-text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import {mapGetters, mapActions} from 'vuex';
  import BSwitch from "buefy/src/components/switch/Switch";
  import {beautifyObjectName, formatUptime} from "../../utils/utils";
  import BSelect from "buefy/src/components/select/Select";
  import CameraViewer from '../main/CameraViewer';
  import ArticleList from '../main/ArticleList';
  import LongText from '../main/LongText';
  import ActionButton from '../main/ActionButton';
  import ControlSlider from '../main/ControlSlider';
  import ControlDropdown from '../main/ControlDropdown';
  import {TemperatureSetpointValues} from "../../utils/constants";

  export default {
    components: {
      BSelect,
      BSwitch,
      appCameraViewer: CameraViewer,
      appArticleList: ArticleList,
      appLongText: LongText,
      appActionButton: ActionButton,
      appControlSlider: ControlSlider,
      appControlDropdown: ControlDropdown
    },
    name: "group-item",
    props: ['data'],
    data() {
      return {
        switchValue: 0,
        dropdownValue: 0,
        sliderValue: 0,
        stateValue: '-',
        valueForDisabledStateTopic: false,
        valueForDropdownMapping: ''
      };
    },
    watch: {
      serialized: {
        immediate: true,
        handler() {
          const valueForTopic = this.mapping[this.topicForObject];
          this.switchValue = 0;
          this.dropdownValue = 0;
          this.sliderValue = 0;
          this.stateValue = '-';
          this.valueForDisabledStateTopic = false;
          this.valueForDropdownMapping = '';
          if (typeof valueForTopic !== 'undefined') {
            this.switchValue = parseInt(valueForTopic);
            this.dropdownValue = valueForTopic;
            this.stateValue = valueForTopic;
            this.sliderValue = parseFloat(valueForTopic);
          }
          if (this.data.disabled_state_topic.length > 0) {
            const valueForDisabledStateTopic = this.mapping[this.data.disabled_state_topic];
            this.valueForDisabledStateTopic = parseInt(valueForDisabledStateTopic) === 1;
          }
          if (this.data.object_type_id === 38 && this.data.object_extra && this.data.object_extra.length > 0) {
            this.valueForDropdownMapping = this.mapping[this.data.object_extra];
          }
        }
      }
    },
    computed: {
      ...mapGetters('mqtt', ['serialized', 'mapping', 'mqttConnected', 'commandTopic']),
      ...mapGetters('cameras', ['cameraById']),
      name() {
        return this.data.alias ? this.data.alias : beautifyObjectName(this.data.object_name);
      },
      urlForLink() {
        // return `/events?objectTypeId=${this.data.object_type_id}&objectId=${this.data.object_id}&timeFrameId=1`;
        return `/view/${this.data.object_id}`;
      },
      hasSwitch() {
        let hasCommandAssigned = false;
        if (parseInt(this.data.command_call_endpoint) !== 0) {
          hasCommandAssigned = true;
        }
        if (!hasCommandAssigned) {
          return false;
        }

        return this.data.object_type_id === 7 || this.data.object_type_id === 16 || this.data.object_type_id === 19;
      },
      hasDropdown() {
        return this.data.object_type_id === 30;
      },
      hasActionButton() {
        return this.data.object_type_id === 36;
      },
      hasControlSlider() {
        return this.data.object_type_id === 37;
      },
      hasControlDropdown() {
        return this.data.object_type_id === 38;
      },
      isCameraViewer() {
        return this.data.object_type_id === 31;
      },
      isArticleList() {
        return this.data.object_type_id === 34;
      },
      camera() {
        return this.cameraById(this.data.camera_id);
      },
      dropdownSourceValues() {
        if (this.hasDropdown && this.data.value_translations === 'setpoint') {
          return this.temperatureSetpointValues;
        }

        return [];
      },
      dropdownOptionSufix() {
        if (this.data.value_translations === 'setpoint') {
          return ' °C';
        }

        return '';
      },
      switchType() {
        return 'is-primary';
      },
      topicForObject() {
        return 'live/objects/' + this.data.object_id + '/state/value';
      },
      topicForCommand() {
        const commandType = 'endpoints';
        return this.commandTopic.replace(/#/, '') + 'objects/' + this.data.object_id + '/' + commandType + '/' + this.data.command_call_endpoint;
      },
      temperatureSetpointValues() {
        return TemperatureSetpointValues;
      },
      classNameToUse() {
        if (this.isArticleList) {
          return 'group_item no_bottom_border';
        }

        return 'group_item';
      },
      parsedExtraToPresets() {
        if (this.data.object_extra.length > 0) {
          try {
            const presets = [];
            const splits = this.data.object_extra.split(/;/);
            splits.forEach(pair => {
              const splits = pair.split(/=/);
              if (splits.length === 2 && !isNaN(parseFloat(splits[1]))) {
                presets.push({
                  value: parseFloat(splits[1]),
                  label: splits[0]
                });
              }
            });

            return presets;
          } catch (e) {
            /* eslint-disable */
            console.log(e);
          }
        }
        return null;
      }
    },
    methods: {
      ...mapActions('mqtt', ['publishCommand']),
      translateStateValue(value) {
        if (this.data.value_translations) {
          if (this.data.value_translations === 'timestamp') {
            const momEvent = moment.unix(value);
            if (!momEvent._isValid || parseInt(value) === 0) {
              return '-';
            }
            const today = moment().format('YYYY-MM-DD');
            const dayEvent = momEvent.format('YYYY-MM-DD');
            return today === dayEvent ? momEvent.format('HH:mm') : momEvent.format('YYYY-MM-DD HH:mm');
          } else if (this.data.value_translations === 'percent') {
            return value + ' %';
          } else if (this.data.value_translations === 'temperature') {
            return value + ' °C';
          } else if (this.data.value_translations === 'watts') {
            return value + ' W';
          } else if (this.data.value_translations === 'speed') {
            return value + ' km/h';
          } else if (this.data.value_translations === 'pressure') {
            return value + ' hPa';
          } else if (this.data.value_translations === 'uptime') {
            return formatUptime(value);
          } else {
            const splits = this.data.value_translations.split(/;/g);
            if (splits.length > 0) {
              const map = {};
              splits.forEach(item => {
                const splits2 = item.split(/=/g);
                if (typeof splits2[1] !== 'undefined') {
                  map[splits2[0]] = splits2[1];
                }
              });

              if (typeof map[value] !== 'undefined') {
                return map[value];
              }
            }
          }
        }

        if (this.data.object_suffix) {
          return `${value} ${this.data.object_suffix}`;
        }

        return value;
      },
      handleClick() {
        if (!this.mqttConnected) {
          return;
        }

        const message = this.switchValue ? 0 : 1;
        this.publishCommand({
          topic: this.topicForCommand,
          message: message
        });
      },
      handleActionButtonClick() {
        if (!this.mqttConnected) {
          return;
        }

        this.publishCommand({
          topic: this.topicForCommand,
          message: 1
        });
      },
      handleChange(event) {
        this.publishCommand({
          topic: this.topicForCommand,
          message: event.target.value
        });
      },
      handleSliderChange(value) {
        this.publishCommand({
          topic: this.topicForCommand,
          message: value
        });
      },
      handleDropdownValueChange(value) {
        this.publishCommand({
          topic: this.topicForCommand,
          message: value
        });
      }
    },
    mounted() {
    }
  }
</script>

<style scoped lang="scss">
  @import "~bulma/sass/utilities/_all";

  .group_item {
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
    padding: 0.1rem 0.1rem 0.25rem 0.1rem;
    border-bottom: dotted 1px $grey-darker;
  }

  .group_item.no_bottom_border {
    border-bottom: none;
  }

  .group_item_simple {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .left {
    display: flex;
  }

  .right {
    display: flex;
    justify-content: flex-end;
  }

  .right .stateValue {
    padding-right: 0.5rem;
  }

  .icon {

  }
</style>
